<template>
  <section
    id="overview"
    class="tw-px-4 tw-pb-8 overview overview-page tw-overflow-y-scroll tw-min-h-screen"
  >
    <div class="overview-header">
      <div class="overview-header__title">
        <h1 class="overview-title">Overview</h1>
        <p class="overview-subtitle">
          Business wealth snapshot, performance, and activity.
        </p>
      </div>
      <div class="overview-header__actions">
        <div class="currency-switcher">
          <button
            v-for="curr in currencies"
            :key="curr.value"
            class="currency-switcher__btn"
            :class="{ 'currency-switcher__btn--active': selectedCurrency === curr.value }"
            @click="switchCurrency(curr.value)"
          >
            <span class="currency-switcher__flag">{{ curr.flag }}</span>
            {{ curr.value }}
          </button>
        </div>
        <LoanConfigIdCopy
          :id="specificLoanConfig"
          :label="`Loan Config ID (${selectedCurrency})`"
          variant="card"
        />
      </div>
    </div>

    <section
      v-if="isSubscribedToProducts('INVEST') && hasPermission('invest:*')"
      class="overview-section tw-mt-10"
    >
      <div
        class="md:tw-grid md:tw-grid-cols-2 md:tw-gap-x-4 xl:tw-gap-x-8 tw-my-8"
      >
        <OverviewCardNew
          v-if="filteredWalletBalance && filteredWalletBalance.length > 0"
          :walletBalance="filteredWalletBalance"
          :selectedCurrency="selectedCurrency"
          @fund-wallet="showFundModal = !showFundModal"
        />

        <OverviewCardNew
          class="tw-mt-6 md:tw-mt-0"
          header="token"
          :tokens="tokens"
          :selectedCurrency="selectedCurrency"
        />
      </div>

      <div class="new-in-section">
        <div class="new-in-header">
          <div class="new-in-header__left">
            <div class="new-in-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#4D84FF"/>
              </svg>
            </div>
            <div>
              <h3 class="new-in-title">New Opportunities</h3>
              <p class="new-in-subtitle">Be among the first to invest in promising deals</p>
            </div>
          </div>
          <router-link 
            :to="{ name: 'Trade', params: { option: 'Now Raising' } }"
            class="new-in-view-all"
          >
            View all
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </router-link>
        </div>
        <ListingCard />
      </div>
    </section>

    <section
      v-if="isSubscribedToProducts('CREDIT') && hasPermission('credit:*')"
      class="overview-section tw-mt-10"
    >
      <div class="section-header">
        <div>
          <h2 class="section-title">Loan summary</h2>
          <p class="section-subtitle">
            Key lending performance and repayment metrics.
          </p>
        </div>
      </div>
      <div class="loan-summary-container">
        <div class="loan-summary">
          <div class="summary-card">
            <div>
              <h3 class="summary-label">Loans Disbursed</h3>
              <p class="summary-number">
                {{ filteredLoanSummary?.totalLoans }}
              </p>
            </div>
            <p class="summary-detail">
              Total Loans Disbursed:
              <span class="summary-accent">{{ formattedDisbursedAmount }}</span>
            </p>
          </div>
          <div class="summary-card">
            <div>
              <h3 class="summary-label">Total Borrowers</h3>
              <p class="summary-number">
                {{ filteredLoanSummary?.totalBorrowers }}
              </p>
            </div>
            <p class="summary-detail">
              Repay Rate:
              <span class="summary-accent">{{
                filteredLoanSummary?.repayRate
                  ? filteredLoanSummary.repayRate.toFixed(2) + "%"
                  : "0%"
              }}</span>
            </p>
          </div>
          <div class="summary-card">
            <div>
              <h3 class="summary-label">Total Repaid Loans</h3>
              <p class="summary-number">
                {{ filteredLoanSummary?.totalRepaidLoans }}
              </p>
            </div>
            <p class="summary-detail">
              Total Repaid Amount:
              <span class="summary-accent">{{ formattedRepaidAmount }}</span>
            </p>
          </div>
        </div>
      </div>
    </section>

    <section
      v-if="isSubscribedToProducts('PRIVATE_RAISE') && hasPermission('deal:*')"
      class="overview-section tw-mt-10"
    >
      <div class="section-header">
        <div>
          <h2 class="section-title">Private raise</h2>
          <p class="section-subtitle">Track allocation, members, and momentum.</p>
        </div>
      </div>
      <section
        class="overview-cards__section tw-flex tw-flex-wrap tw-gap-y-3 md:tw-gap-y-0 md:tw-gap-x-5"
      >
        <OverviewCard
          class="overview-cards__section"
          header="Amount raised"
          :balance="privateRaise.amountRaised"
          :currency="selectedCurrency"
          subText="Target raise"
          :subAmount="privateRaise.targetRaise"
          :hasButton="false"
          buttonText="Increase Allocation"
        />
        <div
          v-if="dealroomMembers"
          class="overview-cards__section section-card tw-w-full tw-mt-3 md:tw-mt-0 tw-py-4 md:tw-py-[10px] tw-px-6 md:tw-px-12 md:tw-px-[40px] lg:tw-flex-grow lg:tw-flex tw-flex-wrap tw-items-center"
        >
          <template v-if="dealroomMembers.length === 0">
            <p class="tw-text-[#4C5B90] tw-mb-5 tw-w-full">
              Membership strength:
              <span
                class="tw-text-secondary tw-text-sm tw-font-CircularStd-medium tw-font-medium"
                >{{ convertToLocale(totalMembers) }}</span
              >
            </p>
            <DealRoomInvite title="Add members" />
          </template>
          <template v-else>
            <div
              class="tw-flex tw-flex-wrap tw-justify-between tw-flex-wrap tw-gap-x-5 tw-items-center tw-gap-y-5"
            >
              <p class="tw-text-[#4C5B90] tw-mt-5 tw-w-full">
                Membership strength:
                <span
                  class="tw-text-secondary tw-text-sm tw-font-CircularStd-medium tw-font-medium"
                  >{{ convertToLocale(totalMembers) }}</span
                >
              </p>
              <div class="tw-flex tw-flex-wrap tw-gap-x-2 md:tw-gap-y-2">
                <div
                  :class="{
                    'tw-relative': index === dealroomMembers.length - 1,
                  }"
                  v-for="(member, index) in selectedMembers"
                  :key="member._id"
                >
                  <MemberIcon :index="index" :member="member.user" />
                  <div
                    class="tw-w-8 tw-h-8 md:tw-w-12 md:tw-h-12 tw-absolute tw-top-0 tw-border tw-rounded-full tw-flex tw-justify-center tw-text-white tw-items-center overlay tw-relative"
                    v-if="
                      index === dealroomMembers.length - 1 &&
                      totalMembers > dealroomMembers.length
                    "
                  >
                    {{ totalMembers }}
                  </div>
                </div>
                <DealRoomInvite title="Add members" :buttonType="'round'" />
              </div>
            </div>
          </template>
        </div>
      </section>
    </section>

    <section
      v-if="isSubscribedToProducts('PUBLIC_RAISE') && hasPermission('deal:*')"
      class="overview-section tw-mt-10"
    >
      <div class="section-header">
        <div>
          <h2 class="section-title">Public raise</h2>
          <p class="section-subtitle">Monitor market pricing and progress.</p>
        </div>
      </div>
      <section
        class="overview-cards__section md:tw-flex tw-overflow-x-scroll tw-gap-5"
      >
        <OverviewCard
          header="Amount raised"
          :balance="publicRaise.amountRaised"
          subText="Target raise"
          :subAmount="publicRaise.targetRaise"
          :hasButton="true"
          :currency="selectedCurrency"
          buttonText="Increase Allocation"
        />
        <OverviewCard
          header="Secondary Valuation"
          :balance="publicRaise.secondaryValuation"
          subText="Listing Valuation"
          :currency="selectedCurrency"
          :subAmount="publicRaise.listingValuation"
        />
      </section>
    </section>

    <section
      class="overview-section tw-mt-10"
      v-if="isSubscribedToProducts('API_KEY') && hasPermission('api:*')"
    >
      <div class="section-header">
        <div>
          <h2 class="section-title">API client</h2>
          <p class="section-subtitle">Usage, customers, and transaction volume.</p>
        </div>
      </div>
      <div class="api-stats-grid">
        <ApiClientCard
          title="Customers"
          :value="totalCustomers"
          valueType="Number"
        />
        <ApiClientCard
          title="Transaction Volume"
          :value="totalVolume"
          valueType="Money"
        />
      </div>
    </section>

    <section
      class="overview-section tw-mt-10"
      v-if="
        isSubscribedToProducts('ESOP') && esoOverview && hasPermission('deal:*')
      "
    >
      <div class="section-header">
        <div>
          <h2 class="section-title">Stock options</h2>
          <p class="section-subtitle">Employee equity, allocations, and value.</p>
        </div>
      </div>
      <div
        class="esop-stats-grid"
        v-if="vestingSchedule"
      >
        <div class="esop-stats-header">
          <router-link
            class="view-benefits-link"
            :to="{ name: 'EmployeeBenefits' }"
          >
            Go to Employee Benefits
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </router-link>
        </div>
        <div class="esop-stats-cards">
          <EsopInfoCard title="Number of employees" :value="employees" />
          <EsopInfoCard title="Market price" :value="marketPrice" />
          <EsopInfoCard
            title="Allocated shares"
            :value="employeesStockOverview.totalAllocatedShares"
          />
          <EsopInfoCard
            title="Exercised shares"
            :value="employeesStockOverview.totalSharesExercised"
          />
        </div>
      </div>
      <div
        class="tw-bg-white tw-h-64 tw-flex tw-items-center tw-justify-center"
        v-else
      >
        <EmptyState name="employeeBenefits">
          <template v-slot:message>
            <div class="tw-max-w-sm">
              <p
                class="tw-font-bold tw-text-lg tw-mb-2 tw-text-secondary tw-tracking-[-0.1px] tw-font-medium tw-text-center tw-mt-5"
              >
                Nothing to see here, yet.
              </p>
              <p
                class="message tw-text-center tw-text-[#4C5B90] tw-my-3 tw-text-sm tw-font-light"
              >
                Employee benefits will show here when you set it up.
              </p>
            </div>
          </template>
        </EmptyState>
      </div>
    </section>

    <section class="overview-section tw-mt-10">
      <div class="section-header">
        <div>
          <h2 class="section-title">Recent transactions</h2>
          <p class="section-subtitle">Latest wallet and token activity.</p>
        </div>
        <router-link
          v-if="transactions && transactions.length > 1"
          class="view-all-link"
          :to="{ name: 'Wallet' }"
        >
          View all
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </router-link>
      </div>
      <div
        class="transactions-card"
        v-if="transactions && transactions.length > 1"
      >
        <div class="transaction-list">
          <div 
            v-for="(transaction, index) in transactions.slice(0, 5)" 
            :key="'txn-' + index"
            class="transaction-item"
          >
            <div class="transaction-icon" :class="getTransactionIconClass(transaction)">
              <svg v-if="isIncomingTransaction(transaction)" width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M12 5V19M12 19L5 12M12 19L19 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M12 19V5M12 5L5 12M12 5L19 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="transaction-details">
              <p class="transaction-type">{{ formatTransactionType(transaction) }}</p>
              <p class="transaction-date">{{ formatISODate(transaction.createdAt) }} · {{ formatISOTime(transaction.createdAt) }}</p>
            </div>
            <div class="transaction-amount" :class="getTransactionAmountClass(transaction)">
              <p class="amount-value">{{ formatTransactionAmount(transaction) }}</p>
              <span class="transaction-status" :class="'status--' + (transaction.status || 'completed').toLowerCase()">
                {{ transaction.status || 'Completed' }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div
        class="transactions-card transactions-empty"
        v-else
      >
        <div class="empty-state">
          <div class="empty-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <p class="empty-title">No transactions yet</p>
          <p class="empty-subtitle">Orders will show up here when someone buys or sells your tokens.</p>
        </div>
      </div>
    </section>

    <ProductsBanner v-if="employeesSummary" class="tw-mt-10" />

    <FundWalletModal :visible="showFundModal" @close="showFundModal = false" />
  </section>
</template>

<script>
import ApiClientCard from "@/components/apiClient/ApiClientCard";
import EmptyState from "@/components/notification/EmptyState";
import EsopInfoCard from "@/components/overview/EsopInfoCard";
import OverviewCard from "@/components/overview/OverviewCard";
import OverviewCardNew from "@/components/overview/OverviewCardNew";
import ProductsBanner from "@/components/overview/ProductsBanner";
import DealRoomInvite from "@/components/privateRaise/DealRoomInvite";
import MemberIcon from "@/components/privateRaise/MemberIcon";
import TransactionTable from "@/components/tables/TransactionTable";
import FundWalletModal from "@/components/wallet/FundModal";
import ListingCard from "@/components/trade/ListingCard";
import LoanConfigIdCopy from "@/components/general/LoanConfigIdCopy";
import { getItem } from "@/utils/storage";
import {
  convertToLocale,
  formatAmountToDollar,
  formatISODate,
  formatISOTime,
  removeUnderscore,
  truncate,
} from "@/utils/helpers";
import { mapActions, mapGetters } from "vuex";
import { permissionsService } from "@/utils/permissionChecker.js";

export default {
  name: "OverviewDetails",

  components: {
    OverviewCard,
    OverviewCardNew,
    TransactionTable,
    ApiClientCard,
    EsopInfoCard,
    EmptyState,
    ProductsBanner,
    DealRoomInvite,
    MemberIcon,
    ListingCard,
    FundWalletModal,
    LoanConfigIdCopy,
  },

  data() {
    return {
      members: {},
      userPermissions: [],
      showApiModal: false,
      showFundModal: false,
      selectedCurrency: "USD",
      currencies: [
        { value: "USD", flag: "🇺🇸", name: "US Dollar" },
        { value: "NGN", flag: "🇳🇬", name: "Nigerian Naira" },
        { value: "KES", flag: "🇰🇪", name: "Kenyan Shilling" },
      ],
      publicRaise: {
        secondaryValuation: 0,
        listingValuation: 0,
        amountRaised: 0,
        targetRaise: 0,
        currency: "USD",
      },
      privateRaise: {
        secondaryValuation: 0,
        listingValuation: 0,
        amountRaised: 0,
        targetRaise: 0,
        currency: "USD",
      },
      tableHeaders: ["", "Details", "Reference", "Amount", "Status", "misc"],
      tableRows: [],
      totalMembers: 0,
    };
  },

  computed: {
    ...mapGetters("organisationModule", [
      "transactions",
      "walletBalance",
      "TransactionPage",
      "TransactionPages",
      "products",
      "tokens",
      "organisationDetails",
      "loanSummary",
      "loanConfiguration",
    ]),
    ...mapGetters("TokenModule", ["dealroomMembers"]),
    ...mapGetters("Esop", ["esoOverview", "esopToken", "vestingSchedule"]),
    ...mapGetters("ApiClient", ["totalCustomers", "totalTransactionVolume"]),
    specificLoanConfig() {
      if (!Array.isArray(this.loanConfiguration)) {
        return "nil";
      }
      return (
        this.loanConfiguration.find((loan) => loan?.currency === this.selectedCurrency)?.id ??
        "nil"
      );
    },
    totalVolume() {
      if (!this.totalTransactionVolume || this.totalTransactionVolume.length === 0) {
        return 0;
      }
      // Find volume for selected currency
      const currencyVolume = this.totalTransactionVolume.find(
        (vol) => vol.currency === this.selectedCurrency
      );
      return currencyVolume ? currencyVolume.totalVolume : 0;
    },
    employeesSummary() {
      return this.esoOverview?.statusOverview;
    },
    employees() {
      return this.employeesSummary.accepted + this.employeesSummary.pending;
    },
    employeesStockOverview() {
      return this.esoOverview?.employeeStockOverview[0];
    },
    marketPrice() {
      return (
        Number(this.esopToken?.valuation) / Number(this.esopToken?.total_supply)
      );
    },
    selectedMembers() {
      const members = this.dealroomMembers?.slice(0, 4);
      return members;
    },
    loanRate() {
      if (this.loanSummary.totalBorrowers === 0) return "0%";
      const rate =
        (this.loanSummary.totalLoans / this.loanSummary.totalBorrowers) * 100;
      return `${rate.toFixed(0)}%`;
    },

    formattedRepaidAmount() {
      const currencyLocale = this.getCurrencyLocale(this.selectedCurrency);
      return new Intl.NumberFormat(currencyLocale.locale, {
        style: "currency",
        currency: this.selectedCurrency,
        minimumFractionDigits: 2,
      }).format(this.filteredLoanSummary.totalRepaidAmount || 0);
    },

    formattedDisbursedAmount() {
      const currencyLocale = this.getCurrencyLocale(this.selectedCurrency);
      return new Intl.NumberFormat(currencyLocale.locale, {
        style: "currency",
        currency: this.selectedCurrency,
        minimumFractionDigits: 2,
      }).format(this.filteredLoanSummary.totalDisbursedAmount || 0);
    },
    
    filteredLoanSummary() {
      // Filter loan summary by selected currency
      // If API supports currency filtering, this would use filtered data
      // For now, return the general summary
      return this.loanSummary || {
        totalLoans: 0,
        totalBorrowers: 0,
        repayRate: 0,
        totalRepaidLoans: 0,
        totalRepaidAmount: 0,
        totalDisbursedAmount: 0,
      };
    },
    
    filteredWalletBalance() {
      // Return full wallet balance array for dropdown population
      if (!this.walletBalance) return [];
      
      // If walletBalance is an array, return the full array
      if (Array.isArray(this.walletBalance)) {
        return this.walletBalance;
      }
      
      // If it's a single object, wrap it in an array
      return [this.walletBalance];
    },
  },

  created() {
    this.getOverviewDetails();
    this.loadUserPermissions();
    this.getLoanConfiguration();
  },
  mounted() {
    if (!this.organisationDetails.verified) {
      this.$router.push({
        name: "CodeVerification",
        params: { type: "private-raise" },
      });
    }

    console.log(this.tokens, "tokens");
  },

  methods: {
    ...mapActions(["showToast"]),
    ...mapActions("organisationModule", [
      "getTransactions",
      "getWalletBalance",
      "getTokens",
      "getFeatures",
      "getOrganisationProfile",
      "getLoanSummary",
      "getLoanConfiguration",
    ]),
    ...mapActions("Esop", [
      "getEsoOverviewData",
      "getVestingSchedule",
      "getEsopToken",
    ]),
    ...mapActions("TokenModule", ["getDealRoomMembers"]),
    ...mapActions("ApiClient", ["getCustomers", "getTotalTransactionVolume"]),

    convertToLocale,
    formatISODate,
    formatISOTime,

    getCurrencyLocale(currency) {
      const locales = {
        USD: { locale: "en-US", symbol: "$" },
        NGN: { locale: "en-NG", symbol: "₦" },
        KES: { locale: "en-KE", symbol: "KSh" },
      };
      return locales[currency] || locales.USD;
    },

    switchCurrency(currency) {
      this.selectedCurrency = currency;
      this.filterTokens();
      // Re-fetch loan summary with new currency
      this.getLoanSummary(currency);
      // API client data is filtered locally via totalVolume computed property
    },

    isSubscribedToProducts(product) {
      // API_KEY | PRIVATE_RAISE | INVEST | ESOP | PUBLIC_RAISE | CREDIT
      let subProducts = this.products?.split(" ");
      let sub = subProducts?.indexOf(product);
      if (sub !== -1) return product;
    },

    canPerformAction(action) {
      return permissionsService.hasPermission(action);
    },

    async getOverviewDetails() {
      const query = { page: 1, limit: 5 };
      const membersQuery = { page: 1, limit: 7 };
      const orgTransaction = this.getTransactions(query);
      const balance = this.getWalletBalance();
      const employees = this.getEsoOverviewData({
        // employee_type: "ESOP_EMPLOYEE",
      });
      const orgProfile = this.getOrganisationProfile();
      const esopVesting = this.getVestingSchedule();
      const esopToken = this.getEsopToken();
      const orgTokens = this.getTokens(query);
      const customers = this.getCustomers();
      const transactionVolume = this.getTotalTransactionVolume();
      const features = this.getFeatures();
      const dealroomMembers = this.getDealRoomMembers(membersQuery);
      const loanSummary = this.getLoanSummary(this.selectedCurrency);

      try {
        const response = await Promise.all([
          orgTransaction,
          balance,
          employees,
          esopVesting,
          orgTokens,
          transactionVolume,
          customers,
          esopToken,
          features,
          dealroomMembers,
          orgProfile,
          loanSummary,
        ]);
        this.totalMembers = response[9].data.data.total;
        this.filterTokens();
      } catch (error) {
        return error;
      }
    },
    setImage(e) {
      e.target.src =
        "https://res.cloudinary.com/djalafcj9/image/upload/v1634012152/getequityV2/header-profile-img_kjtkcq.png";
    },

    async gotoPage(page) {
      const query = { page: page, limit: 5, status: this.status };
      try {
        await this.getTransactions(query);
      } catch (error) {
        responseHandler.handleError(error);
      }
    },

    filterTokens() {
      // Reset values
      this.publicRaise = {
        secondaryValuation: 0,
        listingValuation: 0,
        amountRaised: 0,
        targetRaise: 0,
        currency: this.selectedCurrency,
      };
      this.privateRaise = {
        secondaryValuation: 0,
        listingValuation: 0,
        amountRaised: 0,
        targetRaise: 0,
        currency: this.selectedCurrency,
      };
      
      this.tokens.map((token) => {
        if (token.entity !== "ESO_TOKEN") {
          // Filter by selected currency
          const tokenCurrency = token.currency || "USD";
          if (tokenCurrency !== this.selectedCurrency) return;
          
          if (token.type === "Public") {
            this.publicRaise.secondaryValuation = token.post_raise_valuation;
            this.publicRaise.listingValuation = token.valuation;
            this.publicRaise.amountRaised = token.total_raised * 10;
            this.publicRaise.targetRaise = token.raise_amount;
            this.publicRaise.currency = token.currency;
          }

          if (token.type === "Private") {
            this.privateRaise.secondaryValuation += token.post_raise_valuation;
            this.privateRaise.listingValuation += token.valuation;
            this.privateRaise.amountRaised += token.total_raised * 10;
            this.privateRaise.targetRaise += token.raise_amount;
            this.privateRaise.currency = token.currency;
          }
        }
      });
    },

    updateTableRow() {
      this.tableRows = this.transactions.map((transaction) => {
        let type = removeUnderscore(transaction.type);
        switch (type) {
          case "Debit Sell Wallet":
            type = "Debit Wallet";
            break;

          case "Fund Sell Wallet":
            type = "Fund Wallet";
            break;
          default:
            break;
        }

        let name = `${transaction.token.name}`;
        switch (name) {
          case "GetEquity USD Token":
            name = "USD";
            break;
          default:
            break;
        }
        let volume = transaction.volume;
        let details;
        if (type === "Plan") {
          details = truncate(`${type}: ${transaction.price} ${name}`, 30);
        } else {
          details = truncate(`${type}: ${volume} ${name}`, 30);
        }
        let amount;
        if (transaction.price) {
          amount = transaction.price * transaction.volume;
        } else {
          amount = transaction.token?.price.buy * transaction.volume;
        }
        return {
          details: `<span class="tw-font-bold">${details}</span> <br />
          <span class="tw-text-black-light tw-text-xs">${formatISODate(
            transaction.createdAt
          )}  ${formatISOTime(transaction.createdAt)}</span>`,
          reference:
            type === "Fee" ||
            type === "Debit Wallet" ||
            type === "Send" ||
            type === "Withdraw" ||
            type === "Sell" ||
            type === "Plan"
              ? `<span class="tw-text-xs tw-mb-1">${truncate(
                  transaction.reference,
                  25
                )}</span> <br />
          <span class="tw-text-xs tw-text-error">Debit</span>`
              : `<span class="tw-text-xs tw-mb-1">${truncate(
                  transaction.reference,
                  25
                )}</span> <br />
          <span class="tw-text-xs tw-text-success">Credit</span>`,
          amount: formatAmountToDollar(
            amount,
            2,
            transaction.currency || transaction.token?.currency || "USD"
          ),
          // type: `<span class="tw-hidden">${type}</span>`,
          // image: `<span class="tw-hidden">${transaction.token?.image}</span>`,
          status: transaction.status,
          misc: `misc/${transaction._id}/${type}`,
        };
      });
    },

    loadUserPermissions() {
      const teamMember = getItem("team_member");

      if (teamMember) {
        const parsedPermissions =
          teamMember.groups?.[0]?.permissions?.[0] || [];

        console.log(parsedPermissions, "parsed");
        try {
          if (parsedPermissions) {
            if (parsedPermissions && parsedPermissions?.actions) {
              this.userPermissions = parsedPermissions?.actions;
            }
          }
        } catch (error) {
          console.error("Error parsing permissions:", error);
        }
      }
    },

    hasPermission(permission) {
      if (!this.userPermissions || this.userPermissions.length === 0) {
        return true;
      }

      if (permission.endsWith("*")) {
        const prefix = permission.slice(0, -1);
        return this.userPermissions.some((p) => p.startsWith(prefix));
      }

      return this.userPermissions.includes(permission);
    },

    isIncomingTransaction(transaction) {
      const type = transaction.type?.toLowerCase() || '';
      return type.includes('mint') || 
             type.includes('fund') || 
             type.includes('carry') ||
             type.includes('buy') ||
             type.includes('reversal');
    },

    getTransactionIconClass(transaction) {
      return this.isIncomingTransaction(transaction) ? 'icon--incoming' : 'icon--outgoing';
    },

    getTransactionAmountClass(transaction) {
      return this.isIncomingTransaction(transaction) ? 'amount--incoming' : 'amount--outgoing';
    },

    formatTransactionType(transaction) {
      let type = removeUnderscore(transaction.type || '');
      switch (type) {
        case "Debit Sell Wallet":
          type = "Debit Wallet";
          break;
        case "Fund Sell Wallet":
          type = "Fund Wallet";
          break;
        default:
          break;
      }
      let name = transaction.token?.name || '';
      if (name === "GetEquity USD Token") name = "USD";
      return `${type}${name ? ': ' + name : ''}`;
    },

    formatTransactionAmount(transaction) {
      let amount;
      if (transaction.price) {
        amount = transaction.price * transaction.volume;
      } else {
        amount = (transaction.token?.price?.buy || 0) * transaction.volume;
      }
      const prefix = this.isIncomingTransaction(transaction) ? '+' : '-';
      const currency = transaction.currency || transaction.token?.currency || 'USD';
      return `${prefix}${formatAmountToDollar(amount, 2, currency)}`;
    },
  },

  watch: {
    transactions(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updateTableRow();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.overview-page {
  font-family: "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
  background: #f8fafc;
}

.overview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px 24px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.overview-header__title {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.overview-header__actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.currency-switcher {
  display: flex;
  align-items: center;
  background: #F1F5F9;
  border-radius: 10px;
  padding: 4px;
  gap: 4px;
  
  &__btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    background: transparent;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    color: #64748B;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    
    &:hover {
      color: #0F172A;
      background: rgba(255, 255, 255, 0.5);
    }
    
    &--active {
      background: #ffffff;
      color: #0F172A;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  }
  
  &__flag {
    font-size: 16px;
    line-height: 1;
  }
}

.overview-title {
  font-size: 24px;
  font-weight: 500;
  color: #0f172a;
}

.overview-subtitle {
  font-size: 14px;
  color: #475569;
}

.overview-section {
  display: block;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.section-title {
  font-size: 17px;
  font-weight: 500;
  color: #0f172a;
}

.section-subtitle {
  font-size: 13px;
  color: #475569;
}

.section-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.table-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.overview-cards__section {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none;
  overflow-x: hidden;
  &::-webkit-scrollbar {
    display: none;
  }
  @media (min-width: 768px) {
    flex-basis: 48%;
  }
}
.api {
  &__image {
    @media (min-width: 768px) {
      flex-basis: 40%;
    }
    @media (min-width: 1024px) {
      flex-basis: 30%;
    }
  }
  &__section {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none;
    overflow-x: hidden;
    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.api-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.table {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none;
  overflow-x: hidden;
  &::-webkit-scrollbar {
    display: none;
  }
}

.esop-card__div {
  @media (min-width: 768px) {
    align-items: flex-start;
    flex-basis: 58%;
    align-content: flex-start;
    flex-grow: 2;
  }
  @media (min-width: 1024px) {
    flex-basis: 65%;
  }
  /* max-width: 700px; */
}

.esop-stats-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.esop-stats-header {
  display: flex;
  justify-content: flex-end;
}

.view-benefits-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #4D84FF;
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: #3D6FE0;
  }
  
  svg {
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: translateX(2px);
  }
}

.esop-stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.loan-summary-container {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loan-summary {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.summary-card {
  flex: 1;
  border: 1px solid #e2e8f0;
  background-color: #ffffff;
  padding: 16px;
  text-align: left;
  border-radius: 10px;
  transition: box-shadow 0.2s ease;
}

.summary-card:hover {
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.1);
}

.summary-label {
  font-size: 13px;
  font-weight: 400;
  color: #475569;
}

.summary-number {
  font-size: 28px;
  font-weight: 500;
  margin-bottom: 10px;
  color: #0f172a;
}

.summary-detail {
  border-top: 1px solid #f1f5f9;
  padding: 8px 0 0;
  font-size: 13px;
  font-weight: 400;
  color: #475569;
}

.summary-accent {
  color: #4d84ff;
  font-weight: 500;
}

@media (max-width: 768px) {
  .loan-summary {
    flex-direction: column;
  }

  .summary-card {
    margin: 10px 0;
  }
}

.invest {
  height: 100%;
  background-color: #ffffff;
  border-radius: 10px;
  padding: 20px;
}

// Transaction styles
.view-all-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  color: #4D84FF;
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: #3D6FE0;
    
    svg {
      transform: translateX(2px);
    }
  }
  
  svg {
    transition: transform 0.2s ease;
  }
}

.transactions-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.transactions-empty {
  padding: 48px 24px;
}

.transaction-list {
  display: flex;
  flex-direction: column;
}

.transaction-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #f8fafc;
  }
}

.transaction-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &.icon--incoming {
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  }
  
  &.icon--outgoing {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
}

.transaction-details {
  flex: 1;
  min-width: 0;
}

.transaction-type {
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.transaction-date {
  font-size: 13px;
  color: #94a3b8;
}

.transaction-amount {
  text-align: right;
  flex-shrink: 0;
}

.amount-value {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 2px;
  
  .amount--incoming & {
    color: #10b981;
  }
  
  .amount--outgoing & {
    color: #ef4444;
  }
}

.transaction-status {
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: capitalize;
  
  &.status--completed, &.status--success {
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  }
  
  &.status--pending {
    background: rgba(245, 158, 11, 0.1);
    color: #f59e0b;
  }
  
  &.status--failed {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.empty-icon {
  width: 64px;
  height: 64px;
  background: #f8fafc;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  margin-bottom: 16px;
}

.empty-title {
  font-size: 16px;
  font-weight: 500;
  color: #0f172a;
  margin-bottom: 4px;
}

.empty-subtitle {
  font-size: 14px;
  color: #94a3b8;
  max-width: 280px;
}

// New In Section Styles
.new-in-section {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  margin-top: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.new-in-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
  
  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.new-in-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%);
  border-radius: 12px;
  flex-shrink: 0;
}

.new-in-title {
  font-size: 18px;
  font-weight: 500;
  color: #0f172a;
  margin: 0;
  line-height: 1.3;
}

.new-in-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 2px 0 0 0;
}

.new-in-view-all {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #4D84FF;
  background: #EEF2FF;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: #E0E7FF;
    color: #3B6FE8;
    transform: translateX(2px);
  }
  
  svg {
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: translateX(2px);
  }
}

@media (max-width: 768px) {
  .overview-header {
    padding: 16px;
  }

  .section-card {
    padding: 16px;
  }
  
  .new-in-section {
    padding: 16px;
    border-radius: 12px;
  }
  
  .new-in-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .new-in-icon {
    width: 40px;
    height: 40px;
  }
  
  .new-in-title {
    font-size: 16px;
  }
  
  .transaction-item {
    padding: 12px 16px;
  }
  
  .transaction-icon {
    width: 40px;
    height: 40px;
  }
  
  .transaction-type {
    font-size: 13px;
  }
  
  .transaction-date {
    font-size: 12px;
  }
  
  .amount-value {
    font-size: 13px;
  }
}
</style>
